// webapp/src/components/offlinePrefetch/toast.js
//
// Phase 2a — progress toast for the offline prefetch. Subscribes to the
// three OFFLINE_PREFETCH_* PubSub topics and renders one of four visual
// states: in-progress (spinner + progress bar), success, partial, full
// failure. Tap-to-retry on partial / full failure.

import { h, Component } from 'preact';
import PubSub, { topics } from '../../lib/pubsub';
import { runPrefetch } from '../../lib/offlinePrefetch';

// State machine:
//   null               — hidden
//   { kind: 'progress', index, total, label }
//   { kind: 'success', count }
//   { kind: 'partial', succeeded, failed }
//   { kind: 'failure', failed }

class OfflinePrefetchToast extends Component {
    state = { current: null };
    autoDismissTimer = null;

    componentDidMount() {
        this.subStart    = PubSub.subscribe(topics.OFFLINE_PREFETCH_STARTED, (data) => this.onStart(data));
        this.subProgress = PubSub.subscribe(topics.OFFLINE_PREFETCH_PROGRESS, (data) => this.onProgress(data));
        this.subComplete = PubSub.subscribe(topics.OFFLINE_PREFETCH_COMPLETE, (data) => this.onComplete(data));
    }

    componentWillUnmount() {
        PubSub.unsubscribe(this.subStart);
        PubSub.unsubscribe(this.subProgress);
        PubSub.unsubscribe(this.subComplete);
        this.clearAutoDismiss();
    }

    clearAutoDismiss() {
        if (this.autoDismissTimer) {
            clearTimeout(this.autoDismissTimer);
            this.autoDismissTimer = null;
        }
    }

    scheduleAutoDismiss(ms) {
        this.clearAutoDismiss();
        this.autoDismissTimer = setTimeout(() => this.setState({ current: null }), ms);
    }

    onStart = (data) => {
        // Suppress in-progress visuals in silent mode — only failures will show.
        if (data && data.silent) return;
        this.clearAutoDismiss();
        this.setState({ current: { kind: 'progress', index: 0, total: data.total, label: '' } });
    };

    onProgress = (data) => {
        // Don't promote a hidden toast to visible during silent runs.
        if (!this.state.current) return;
        this.setState({
            current: {
                kind: 'progress',
                index: data.index,
                total: data.total,
                label: data.label,
            },
        });
    };

    onComplete = (data) => {
        const { succeeded = [], failed = [], silent = false } = data || {};
        if (failed.length === 0) {
            if (silent) return; // silent success: no toast at all
            this.setState({ current: { kind: 'success', count: succeeded.length } });
            this.scheduleAutoDismiss(4000);
        } else if (succeeded.length > 0) {
            this.setState({ current: { kind: 'partial', succeeded, failed } });
            this.scheduleAutoDismiss(8000);
        } else {
            this.setState({ current: { kind: 'failure', failed } });
            this.scheduleAutoDismiss(8000);
        }
    };

    onTapRetry = () => {
        const c = this.state.current;
        if (!c) return;
        // Re-run the full prefetch. Per spec, retrying only the failed steps
        // is a v2 nicety; v1 just re-runs everything.
        this.setState({ current: null });
        runPrefetch();
    };

    onTapDismiss = () => {
        this.clearAutoDismiss();
        this.setState({ current: null });
    };

    render() {
        const c = this.state.current;
        if (!c) return null;

        const baseClasses = 'position-fixed start-50 translate-middle-x rounded shadow px-3 py-2 d-flex align-items-center';
        const baseStyle = 'top: 6rem; z-index: 1080; min-width: 16rem; max-width: 32rem;';

        if (c.kind === 'progress') {
            const pct = c.total ? Math.round((c.index / c.total) * 100) : 0;
            return (
                <div class={`${baseClasses} bg-body-secondary text-body`} style={baseStyle} role='status' aria-live='polite'>
                    <i class='fa-solid fa-cloud-arrow-down me-2 fa-fade' />
                    <div class='flex-grow-1'>
                        <div>{`Forbereder offline… (${c.index + 1}/${c.total}${c.label ? ` — ${c.label}` : ''})`}</div>
                        <div class='progress mt-1' style='height: 4px;'>
                            <div class='progress-bar' role='progressbar' style={`width: ${pct}%;`} />
                        </div>
                    </div>
                </div>
            );
        }

        if (c.kind === 'success') {
            return (
                <div class={`${baseClasses} bg-success-subtle text-success-emphasis`} style={baseStyle} role='status' onClick={this.onTapDismiss}>
                    <i class='fa-solid fa-circle-check me-2' />
                    <span>{`Forberedt for offline – ${c.count} endepunkter`}</span>
                </div>
            );
        }

        if (c.kind === 'partial') {
            return (
                <div class={`${baseClasses} bg-warning-subtle text-warning-emphasis`} style={baseStyle} role='status' onClick={this.onTapRetry}>
                    <i class='fa-solid fa-triangle-exclamation me-2' />
                    <span>{`Forberedt for offline (${c.succeeded.length}/${c.succeeded.length + c.failed.length}) — trykk for å prøve igjen`}</span>
                </div>
            );
        }

        if (c.kind === 'failure') {
            return (
                <div class={`${baseClasses} bg-danger-subtle text-danger-emphasis`} style={baseStyle} role='status' onClick={this.onTapRetry}>
                    <i class='fa-solid fa-circle-xmark me-2' />
                    <span>Forberedelse feilet — trykk for å prøve igjen</span>
                </div>
            );
        }

        return null;
    }
}

export default OfflinePrefetchToast;
